import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';
import { User } from './_models/user';
import { AuthenticationService } from './_services/authentication.service';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { NavbarComponent } from './_components/navbar/navbar.component';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, map } from 'rxjs/operators';
import { ToastComponent } from './_components/toast/toast.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [NavbarComponent, RouterOutlet, ToastComponent],
})
export class AppComponent {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly router = inject(Router);
  private intercomToast?: ReturnType<typeof setTimeout>;

  title = 'Ogibiotec';
  appVersion = environment.appVersion;
  backendVersionString = environment.apiUrl;
  currentUser: User;
  currentOrganisation = null;
  currentLab = null;
  displayWarning = false;
  intercomBootFailed = signal(false);

  readonly showNavbar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => {
        let route = this.router.routerState.root;
        while (route.firstChild) route = route.firstChild;
        return !route.snapshot.data['hideNavbar'];
      }),
    ),
    { initialValue: true },
  );

  constructor() {
    this.authenticationService.currentUser$.subscribe((x) => {
      this.currentUser = x;
      if (this.currentUser) {
        this.intercomAuthentication();
      }
    });

    this.authenticationService.selectedLab$.subscribe((x) => {
      this.currentLab = x;
    });

    this.authenticationService.currentOrg$.subscribe((x) => {
      this.currentOrganisation = x;
      if (
        this.currentUser &&
        this.currentUser.organisationId !== this.currentOrganisation
      ) {
        this.displayWarning = true;
      } else {
        this.displayWarning = false;
      }
    });

    // Initialize service worker update handling
    this.initializeServiceWorkerUpdates();
  }

  private intercomAuthentication(): void {
    this.authenticationService.authenticateUserIntercom().subscribe({
      next: (userAuthenticated: boolean) => {
        if (userAuthenticated) {
          this.authenticationService.bootUser().catch(() => {
            this.showToast();
          });
        }
      },
      error: () => {
        // Silently fail - user can still use the application
      },
    });
  }

  private initializeServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker updates are not enabled');
      return;
    }

    // Check for updates immediately when app starts
    this.checkForUpdates();

    // Listen for available updates
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
      )
      .subscribe((evt) => {
        console.log('New version available:', evt.latestVersion.hash);
        this.activateUpdate();
      });

    // Handle unrecoverable state - reload the page
    this.swUpdate.unrecoverable.subscribe(() => {
      console.log('Service Worker in unrecoverable state, reloading page');
      window.location.reload();
    });

    // Periodic update checks every 6 hours
    setInterval(
      () => {
        this.checkForUpdates();
      },
      6 * 60 * 60 * 1000,
    ); // 6 hours in milliseconds
  }

  showToast() {
    if (this.intercomToast) {
      clearTimeout(this.intercomToast);
    }
    this.intercomBootFailed.set(true);
    this.intercomToast = setTimeout(() => {
      this.intercomBootFailed.set(false);
    }, 5000);
  }

  private checkForUpdates(): void {
    if (this.swUpdate.isEnabled) {
      this.swUpdate
        .checkForUpdate()
        .then((updateAvailable) => {
          if (updateAvailable) {
            console.log('Update check: new version detected');
          } else {
            console.log('Update check: no updates available');
          }
        })
        .catch((error) => {
          console.error('Error checking for updates:', error);
        });
    }
  }

  private activateUpdate(): void {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        console.log('Update activated successfully, reloading application');
        // Small delay to ensure service worker is properly updated
        setTimeout(() => {
          window.location.reload();
        }, 100);
      })
      .catch((error) => {
        console.error('Error activating update:', error);
      });
  }
}
